import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/stores/auth.store';
import { useTheme } from '@/stores/theme.store';
import { useWebSocket } from '@/lib/ws/useWebSocket';

import { TooltipProvider } from '@/components/ui/tooltip';
import { CommandPalette } from '@/components/shortcuts/CommandPalette';
import { Composer } from '@/components/compose/Composer';

import { LoginPage } from '@/pages/Login';
import { RegisterPage } from '@/pages/Register';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { SetupPage } from '@/pages/Setup';
import { InboxPage } from '@/pages/Inbox';
import { DraftsPage } from '@/pages/Drafts';
import { SettingsPage } from '@/pages/Settings';
import { ContactsPage } from '@/pages/Contacts';
import { DocumentsPage } from '@/pages/Documents';
import { RulesPage } from '@/pages/Rules';
import { InboxZeroPage } from '@/pages/InboxZero';
import { CalendarPage } from '@/pages/Calendar';
import { MeetingsPage } from '@/pages/Meetings';
import { ArchivePage } from '@/pages/Archive';
import { BillingPage } from '@/pages/Billing';
import { DomainsPage } from '@/pages/Domains';
import { VerifyEmailPage } from '@/pages/VerifyEmail';
import { FileDownloadPage } from '@/pages/FileDownload';
import { AiRepliesPage } from '@/pages/AiReplies';
import { MeetPage } from '@/pages/Meet';
import { ReceptionPage } from '@/pages/Reception';
import { DeliveryPage } from '@/pages/Delivery';
import { AdminPage } from '@/pages/Admin';
import { CallPage } from '@/pages/Call';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);
  const location = useLocation();
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AuthenticatedShell() {
  // Hook attivato solo dopo login
  useWebSocket();
  return (
    <>
      <CommandPalette />
      <AnimatePresence>
        <Composer />
      </AnimatePresence>
    </>
  );
}

export function App() {
  const loadCurrentUser = useAuth((s) => s.loadCurrentUser);
  const applyTheme = useTheme((s) => s.applyTheme);
  const user = useAuth((s) => s.user);

  useEffect(() => {
    void loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    applyTheme();
    // Riapplica se cambia preferenza sistema
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => applyTheme();
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, [applyTheme]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <BrowserRouter>
          {user && <AuthenticatedShell />}
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/meet/:token" element={<MeetPage />} />
            <Route path="/call/:token" element={<CallPage />} />
            <Route path="/f/:token" element={<FileDownloadPage />} />
            <Route path="/verifica/:token" element={<VerifyEmailPage />} />
            <Route path="/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
            <Route path="/drafts" element={<ProtectedRoute><DraftsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
            <Route path="/rules" element={<ProtectedRoute><RulesPage /></ProtectedRoute>} />
            <Route path="/inbox-zero" element={<ProtectedRoute><InboxZeroPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/meetings" element={<ProtectedRoute><MeetingsPage /></ProtectedRoute>} />
            <Route path="/archive" element={<ProtectedRoute><ArchivePage /></ProtectedRoute>} />
            <Route path="/abbonamento" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/domini" element={<ProtectedRoute><DomainsPage /></ProtectedRoute>} />
            <Route path="/ai-replies" element={<ProtectedRoute><AiRepliesPage /></ProtectedRoute>} />
            <Route path="/reception" element={<ProtectedRoute><ReceptionPage /></ProtectedRoute>} />
            <Route path="/delivery" element={<ProtectedRoute><DeliveryPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="bottom-right"
            toastOptions={{
              classNames: {
                toast: 'bg-popover text-popover-foreground border',
              },
            }}
          />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
